import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, Sparkles, Plus, ChevronRight } from "lucide-react";

import { ToolbarSticky } from "@/components/wyvio-ui/signature/ToolbarSticky";
import { KpiCard } from "@/components/wyvio-ui/signature/KpiCard";
import { DossierCard } from "@/components/wyvio-ui/signature/DossierCard";
import { DeliveryCard } from "@/components/wyvio-ui/signature/DeliveryCard";
import { BtnSignature } from "@/components/wyvio-ui/signature/BtnSignature";
import { StatusChip } from "@/components/wyvio-ui/signature/StatusChip";
import { WorkflowBar13 } from "@/components/wyvio-ui/signature/WorkflowBar13";
import { HeroUrgent } from "@/components/wyvio-ui/signature/HeroUrgent";

// Constantes de mockup : aucune photo de démo. L'IA WYVIO gérera automatiquement
// le choix de la meilleure image extérieure côté production.
const DOSSIER_PHOTOS_ENABLED = false;

// WYVIO principe : aucune image de démo. L'IA sélectionne automatiquement
// la meilleure photo extérieure du dossier ; à défaut, une image constructeur ;
// à défaut, la silhouette premium WYVIO ci-dessous.
const DOSSIERS = [
  {
    id: 2,
    client: "Familia Dupont · Livraison Neuilly",
    vehicle: "Mercedes-Benz Classe G",
    plate: "GT-908-BM",
    status: "bloque",
    action: "Relancer paiement",
    urgent: false,
    image: null,
    completed: 4,
    silhouette: "suv",
    photosPending: true,
  },
  {
    id: 3,
    client: "Antoine Vidal · Livraison Lyon",
    vehicle: "Tesla Model S Plaid",
    plate: "TS-741-VL",
    status: "encours",
    action: "Valider transport",
    urgent: false,
    image: null,
    completed: 7,
    silhouette: "berline",
    photosPending: false,
  },
  {
    id: 4,
    client: "Studios Rivian · Marseille",
    vehicle: "Rivian R1T Launch",
    plate: "RV-330-AZ",
    status: "encours",
    action: "Ajouter photos",
    urgent: false,
    image: null,
    completed: 3,
    silhouette: "break",
    photosPending: true,
  },
];

const DELIVERIES = [
  {
    time: "10h30",
    vehicle: "Audi RS6 Performance",
    client: "L. Marchand",
    address: "Paris 16e",
    image: null,
    silhouette: "break",
    isNext: true,
  },
  {
    time: "13h00",
    vehicle: "BMW M4 Competition",
    client: "S. Rossi",
    address: "Boulogne",
    image: null,
    silhouette: "coupe",
  },
  {
    time: "16h45",
    vehicle: "Porsche Taycan Turbo",
    client: "Groupe Élégance",
    address: "Neuilly-sur-Seine",
    image: null,
    silhouette: "berline",
  },
  {
    time: "18h20",
    vehicle: "Range Rover Autobiography",
    client: "A. Beaumont",
    address: "Saint-Cloud",
    image: null,
    silhouette: "suv",
  },
];

const TYPO_CLASSES = {
  "inter-display": "font-inter-body",
  "general-sans": "font-general-sans",
  "neue-haas": "font-neue-haas",
};

const TYPO_LABELS = {
  "inter-display": "Inter Display / Inter",
  "general-sans": "General Sans",
  "neue-haas": "Neue Haas Grotesk (Inter Tight)",
};

const TYPO_TAG = {
  "inter-display": "Variante A · Neutralité Stripe",
  "general-sans": "Variante B · Signature Polestar",
  "neue-haas": "Variante C · Swiss modernism Linear",
};

export default function DashboardMockup({ typography = "inter-display" }) {
  const fontClass = TYPO_CLASSES[typography] || TYPO_CLASSES["inter-display"];

  return (
    <>
      <Helmet>
        <title>{`Mockup Dashboard — ${TYPO_LABELS[typography]} · wyvio`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className={`wyvio-signature-bg min-h-screen ${fontClass}`}>
        {/* Toolbar sticky */}
        <ToolbarSticky />

        {/* Contenu */}
        <main className="relative z-10 mx-auto max-w-[1400px] px-12 pb-32 pt-20">
          {/* Ruban variant (mockup only) */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-14 inline-flex items-center gap-2 rounded-full border border-[#E8E8E2] bg-white/70 px-3.5 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.2em] text-[#6B6B67] backdrop-blur"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#151515]" />
            {TYPO_TAG[typography]}
          </motion.div>

          {/* Hero salutation — CENTRE DE PILOTAGE */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-24 flex items-end justify-between gap-8"
          >
            <div>
              <p className="mb-6 text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#8A8A80]">
                Centre de pilotage · Mardi 24 février
              </p>
              <h1 className="text-[76px] font-medium leading-[0.98] tracking-[-0.032em] text-[#151515]">
                Bonjour Stéphane.
              </h1>
              <p className="mt-8 max-w-[620px] text-[17px] font-normal leading-[1.55] text-[#4A4A46]">
                <span className="text-[#151515]">3 livraisons peuvent être finalisées aujourd&apos;hui.</span>{" "}
                2 dossiers demandent votre attention.{" "}
                <span className="text-[#151515]">Aucun blocage critique.</span>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <BtnSignature variant="ghost" icon={Sparkles} testid="cta-assistant">
                Assistant
              </BtnSignature>
              <BtnSignature variant="primary" arrow testid="cta-primary">
                Nouveau dossier
              </BtnSignature>
            </div>
          </motion.section>

          {/* Hero Urgent — signature visuelle absolue de WYVIO.
              Aucune image de démo : silhouette premium WYVIO variant="dark",
              en attendant que l'IA sélectionne la meilleure photo réelle. */}
          <section className="mb-24" data-testid="section-urgent">
            <HeroUrgent
              testid="hero-urgent"
              dossier={{
                vehicle: "Porsche 911 GT3",
                client: "Marc Ballaud · Retrait boutique",
                plate: "AA-042-XR",
                action: "Proposer 3 créneaux",
                reason:
                  "Le paiement est confirmé, les photos sont validées. Il ne manque plus qu'un rendez-vous pour clôturer la livraison.",
                dueLabel: "Créneau demandé depuis 2h · SLA à J+1",
                location: "Retrait boutique · Deluxe Motors Paris 8e",
                image: null,
                silhouette: "coupe",
                photosPending: true,
                completed: 9,
              }}
            />
          </section>

          {/* KPI row */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mb-28 grid grid-cols-4 gap-6"
            data-testid="kpi-row"
          >
            <KpiCard
              label="Dossiers actifs"
              value={38}
              delta="+12%"
              spark={[22, 25, 24, 28, 30, 33, 38]}
              isPrimary
              testid="kpi-active"
            />
            <KpiCard
              label="Livraisons semaine"
              value={17}
              delta="+3"
              spark={[12, 10, 14, 15, 13, 16, 17]}
              testid="kpi-deliveries"
            />
            <KpiCard
              label="Dossiers bloqués"
              value={2}
              delta="-1"
              spark={[5, 4, 4, 3, 3, 2, 2]}
              testid="kpi-blocked"
            />
            <KpiCard
              label="Chiffre du mois"
              value={284}
              unit="k €"
              delta="+18%"
              spark={[180, 195, 210, 225, 240, 265, 284]}
              testid="kpi-revenue"
            />
          </motion.section>

          {/* Section À traiter */}
          <section className="mb-28" data-testid="section-todo">
            <motion.header
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mb-10 flex items-end justify-between"
            >
              <div>
                <p className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.28em] text-[#8A8A80]">
                  Cockpit du jour
                </p>
                <h2 className="text-[40px] font-medium leading-none tracking-[-0.025em] text-[#151515]">
                  À traiter maintenant
                </h2>
              </div>
              <button className="group inline-flex items-center gap-1.5 text-[13px] font-medium text-[#151515]">
                Voir tous les dossiers
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </motion.header>

            <div className="space-y-5">
              {DOSSIERS.map((d, i) => (
                <DossierCard
                  key={d.id}
                  dossier={d}
                  index={i}
                  testid={`dossier-${d.id}`}
                />
              ))}
            </div>
          </section>

          {/* À venir + Status */}
          <section className="grid grid-cols-3 gap-10" data-testid="section-upcoming">
            {/* À venir */}
            <div className="col-span-2">
              <motion.header
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.35 }}
                className="mb-10 flex items-end justify-between"
              >
                <div>
                  <p className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.28em] text-[#8A8A80]">
                    Planning du jour
                  </p>
                  <h2 className="text-[34px] font-medium leading-none tracking-[-0.025em] text-[#151515]">
                    Livraisons à venir
                  </h2>
                </div>
                <BtnSignature variant="ghost" size="sm" icon={Plus} testid="cta-add-slot">
                  Créneau
                </BtnSignature>
              </motion.header>

              <div className="shadow-wyvio-elev rounded-[24px] bg-white p-3">
                {DELIVERIES.map((d, i) => (
                  <React.Fragment key={i}>
                    <DeliveryCard
                      {...d}
                      index={i}
                      testid={`delivery-${i}`}
                    />
                    {i < DELIVERIES.length - 1 && (
                      <div className="mx-4 h-px bg-[#EEEEE8]" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Panneau Insight — éditorial, monochrome */}
            <motion.aside
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="col-span-1"
            >
              <p className="mb-10 mt-1 text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#8A8A80]">
                Rythme de l&apos;agence
              </p>

              <div className="shadow-wyvio-elev rounded-[24px] bg-white p-10">
                <p className="text-[10.5px] font-medium uppercase tracking-[0.2em] text-[#8A8A80]">
                  Cette semaine
                </p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="wyvio-tabular text-[80px] font-medium leading-[0.9] tracking-[-0.035em] text-[#151515]">
                    3,4
                  </span>
                  <span className="text-[16px] font-normal text-[#8A8A80]">j</span>
                </div>
                <p className="mt-6 text-[14px] font-normal leading-[1.55] text-[#4A4A46]">
                  Délai moyen de livraison.<br />
                  <span className="text-[#151515]">1,2 jour plus rapides</span> que
                  la semaine passée.
                </p>

                <div className="my-10 h-px w-full bg-[#E8E8E2]" />

                <div className="space-y-6">
                  {[
                    { label: "Taux de satisfaction", value: "98 %" },
                    { label: "Photos avant livraison", value: "100 %" },
                    { label: "Dossiers en retard", value: "0" },
                  ].map((r, i) => (
                    <div key={i} className="flex items-baseline justify-between">
                      <span className="text-[12.5px] font-normal text-[#6B6B67]">
                        {r.label}
                      </span>
                      <span className="wyvio-tabular text-[15px] font-medium tracking-[-0.01em] text-[#151515]">
                        {r.value}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  className="group mt-10 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#151515]"
                  data-testid="cta-detail-sante"
                >
                  Voir le rapport hebdomadaire
                  <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
            </motion.aside>
          </section>
        </main>
      </div>
    </>
  );
}
